import { useCallback, useEffect, useState } from 'react';
import { get } from '@vigilo/api-client';
import { useMockMode } from '@/shell/MockModeContext';
import { MOCK_ADMIN_DATA_FEEDS } from '../data/mock';

export type CrowdDataFeedStatus = 'configured' | 'not_configured' | 'standby';

export interface CrowdDataFeedStatusItem {
  id: string;
  label: string;
  boundary: string;
  status: CrowdDataFeedStatus;
  detail: string;
  governance: 'anonymous_only';
  allowedOutputs: string[];
}

export interface CrowdDataFeedsResponse {
  applicationId: 'crowd_monitoring';
  generatedAt: string;
  feeds: CrowdDataFeedStatusItem[];
}

interface AdminDataFeedsHookState {
  data: CrowdDataFeedsResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

function unwrap<T>(body: T | { data?: T }): T {
  const b = body as { data?: T };
  return (b.data !== undefined ? b.data : body) as T;
}

export function useAdminDataFeeds(): AdminDataFeedsHookState {
  const { isMock } = useMockMode();
  const [data, setData] = useState<CrowdDataFeedsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isMock) {
        await new Promise(resolve => setTimeout(resolve, 40));
        setData(MOCK_ADMIN_DATA_FEEDS);
      } else {
        const body = await get<CrowdDataFeedsResponse | { data?: CrowdDataFeedsResponse }>('/crowd/admin/data-feeds');
        setData(unwrap(body));
      }
    } catch {
      setError('Failed to load data feed status');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [isMock]);

  useEffect(() => { refresh(); }, [refresh]);

  return { data, loading, error, refresh };
}
